import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiFileText, FiDownload, FiEye, FiCheckCircle, FiClock, FiXCircle, FiUser, FiBriefcase } from 'react-icons/fi';
import { useAuth } from '../../../store/auth';
import { 
  ShareholderService, 
  ShareholderDocument, 
  ShareholderPassport, 
  ShareholderAddress 
} from '../../../services/ShareholderService';
import { FolderService } from '../../../services/FolderService';
import { FolderDocumentItem } from '../../../services/Interfaces/IFolderService';
import { ICompany } from '../../../services/Interfaces/IAuthService';
import { AuthService } from '../../../services/AuthService';
import { useApi } from '../../../services/hooks/apiHook';
import { AxiosInstance } from 'axios';
import { errorToast } from '../../../utils/toasts';

export const DirectorDocuments: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token, company } = useAuth();
  const api: AxiosInstance = useApi();

  const [activeTab, setActiveTab] = useState<'personal' | 'company'>('personal');
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<ShareholderDocument[]>([]);
  const [passports, setPassports] = useState<ShareholderPassport[]>([]);
  const [addresses, setAddresses] = useState<ShareholderAddress[]>([]);
  const [companyDocuments, setCompanyDocuments] = useState<FolderDocumentItem[]>([]);
  const [userCompany, setUserCompany] = useState<ICompany | null>(null);

  const lastFetchKeyRef = useRef<string | null>(null);
  const lastDocumentsFetchKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const initialLanguageSyncRef = useRef(true);

  // Memoize config to prevent unnecessary re-renders
  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Language': i18n.language.toLowerCase()
      }
    }),
    [token, i18n.language]
  );

  // Fetch user's companies on mount
  const fetchUserCompanies = useCallback(async () => {
    if (!token || isFetchingRef.current) {
      setIsLoading(false);
      return;
    }

    const fetchKey = `${token}|${i18n.language}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    lastFetchKeyRef.current = fetchKey;
    isFetchingRef.current = true;
    
    try {
      const response = await AuthService.allCompanies(api, config);
      
      if (response.data && response.data.length > 0) {
        // Use the first company or the one from context if it exists
        const selectedCompany = company || response.data[0];
        setUserCompany(selectedCompany);
      } else {
        setIsLoading(false); // Stop loading if no companies found
      }
    } catch (error) {
      console.error('Error fetching user companies:', error);
      errorToast(t('messages.errorLoadingCompanies') || 'Error loading companies');
      setIsLoading(false); // Stop loading on error
      lastFetchKeyRef.current = null; // Reset on error to allow retry
    } finally {
      isFetchingRef.current = false;
    }
  }, [token, api, config, i18n.language, company, t]);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    if (initialLanguageSyncRef.current) {
      initialLanguageSyncRef.current = false;
      void fetchUserCompanies();
      return;
    }

    const timeout = setTimeout(() => {
      void fetchUserCompanies();
    }, 700);

    return () => {
      clearTimeout(timeout);
    };
  }, [token, i18n.language, fetchUserCompanies]);

  const fetchDocuments = useCallback(async () => {
    if (!token || !userCompany || isFetchingRef.current) {
      return;
    }

    const companyIdentifier = userCompany?.id || userCompany?.slug;
    const fetchKey = `${token}|${i18n.language}|${companyIdentifier}`;
    if (lastDocumentsFetchKeyRef.current === fetchKey) {
      return;
    }

    lastDocumentsFetchKeyRef.current = fetchKey;
    isFetchingRef.current = true;

    try {
      setIsLoading(true);

      // Fetch personal documents
      const [docsData, passportsData, addressesData] = await Promise.all([
        ShareholderService.getMyDocuments(api, config),
        ShareholderService.getMyPassports(api, config),
        ShareholderService.getMyAddress(api, config)
      ]);

      setDocuments(docsData.data || []);
      setPassports(passportsData.data || []);
      setAddresses(addressesData.data || []);

      // Fetch company documents if company exists
      if (companyIdentifier) {
        try {
          const response = await FolderService.getCompanyFolderDocuments(api, companyIdentifier, config);
          
          if (response.success && response.data.items) {
            setCompanyDocuments(response.data.items);
          } else {
            setCompanyDocuments([]);
          }
        } catch (companyError) {
          console.error('Error fetching company documents:', companyError);
          setCompanyDocuments([]);
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      errorToast(t('messages.errorLoadingDocuments') || 'Error loading documents');
      lastDocumentsFetchKeyRef.current = null; // Reset on error to allow retry
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [token, userCompany, api, config, i18n.language, t]);

  useEffect(() => {
    if (!token || !userCompany) {
      return;
    }

    if (initialLanguageSyncRef.current) {
      initialLanguageSyncRef.current = false;
      void fetchDocuments();
      return;
    }

    const timeout = setTimeout(() => {
      void fetchDocuments();
    }, 700);

    return () => {
      clearTimeout(timeout);
    };
  }, [token, userCompany, i18n.language, fetchDocuments]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <FiCheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <FiXCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
      default:
        return <FiClock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved':
        return t('labels.lblApproved') || 'Approved';
      case 'rejected':
        return t('labels.lblRejected') || 'Rejected';
      case 'pending':
      default:
        return t('labels.lblPending') || 'Pending';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const translateFolderName = useCallback(
    (folderName?: string) => {
      if (!folderName) return '';

      const candidates = [
        folderName,
        folderName?.toLowerCase(),
        folderName?.toUpperCase(),
        folderName?.replace(/\s+/g, ''),
        folderName?.replace(/\s+/g, '_')
      ].filter(Boolean) as string[];

      for (const candidate of candidates) {
        const key = `certificatesDocuments.${candidate}`;
        const translated = t(key);
        if (translated !== key) {
          return translated;
        }
      }

      return folderName;
    },
    [t]
  );

  const handleViewDocument = (url: string) => {
    if (url) {
      // Assuming the backend returns a full URL or we need to prepend the API base URL
      const documentUrl = url.startsWith('http') ? url : `https://offshore2go-documents.s3.us-east-2.amazonaws.com/${url}`;
      window.open(documentUrl, '_blank');
    }
  };

  const handleDownloadDocument = (url: string, name: string) => {
    if (url) {
      const documentUrl = url.startsWith('http') ? url : `https://offshore2go-documents.s3.us-east-2.amazonaws.com/${url}`;
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('labels.lblLoading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  const allPersonalDocumentsEmpty = documents.length === 0 && passports.length === 0 && addresses.length === 0;

  // Show message if no company is linked to the user
  if (!isLoading && !userCompany) {
    return (
      <>
        <div className="flex w-full flex-col border-b-2 pb-3">
          <h2 className="text-xl font-bold text-[#0C1FAA]">
            {t('labels.lblMyDocuments') || 'My Documents'}
          </h2>
        </div>
        <div className="mt-8 bg-yellow-50 rounded-lg p-8 text-center border border-yellow-200">
          <FiFileText className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {t('labels.lblNoCompanyLinked') || 'No Company Linked'}
          </h3>
          <p className="text-gray-600">
            {t('labels.lblNoCompanyLinkedDescription') || 'You are not currently linked to any company. Please contact support if you believe this is an error.'}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col border-b-2 pb-3">
        <h2 className="text-xl font-bold text-[#0C1FAA]">
          {t('labels.lblMyDocuments') || 'My Documents'}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {t('labels.lblViewYourDocuments') || 'View and manage your submitted documents'}
        </p>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('personal')}
            className={`
              py-3 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'personal'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <FiUser className="w-5 h-5" />
              {t('labels.lblPersonalDocuments') || 'Personal Documents'}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('company')}
            className={`
              py-3 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'company'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <FiBriefcase className="w-5 h-5" />
              {t('labels.lblCompanyDocuments') || 'Company Documents'}
            </div>
          </button>
        </nav>
      </div>

      {/* Personal Documents Tab */}
      {activeTab === 'personal' && (
        allPersonalDocumentsEmpty ? (
        <div className="mt-8 bg-blue-50 rounded-lg p-8 text-center">
          <FiFileText className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {t('labels.lblNoDocuments') || 'No Documents Found'}
          </h3>
          <p className="text-gray-600">
            {t('labels.lblNoDocumentsDescription') || 'You haven\'t submitted any documents yet.'}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
            {/* Passport Documents Section */}
            {passports.length > 0 && (
              <section className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiFileText className="w-5 h-5 text-gray-600" />
                      <h3 className="text-lg font-semibold text-gray-800">
                        {t('labels.lblPassport') || 'Passport'}
                      </h3>
                    </div>
                    <span className="text-sm text-gray-600">
                      {passports.length} {t('labels.lblDocuments') || 'documents'}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {passports.map((passport) => (
                      <div
                        key={passport.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="bg-purple-100 p-3 rounded-lg">
                            <FiFileText className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-800">
                              {t('labels.lblPassportNumber') || 'Passport Number'}: {passport.number}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {t('labels.lblIssuingCountry') || 'Issuing Country'}: {passport.issuing_country}
                            </p>
                            <p className="text-xs text-gray-500">
                              {t('labels.lblExpiryDate') || 'Expiry Date'}: {formatDate(passport.expiry_date)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(passport.status)}
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(passport.status)}`}>
                              {getStatusLabel(passport.status)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleViewDocument(passport.document_url)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title={t('labels.lblView') || 'View'}
                          >
                            <FiEye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDownloadDocument(passport.document_url, `passport-${passport.number}.pdf`)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title={t('labels.lblDownload') || 'Download'}
                          >
                            <FiDownload className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Local Document / Identity Document Section */}
          {documents.length > 0 && (
            <section className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FiFileText className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                        {t('labels.lblLocalDocument') || 'Local Document / ID'}
                    </h3>
                  </div>
                  <span className="text-sm text-gray-600">
                    {documents.length} {t('labels.lblDocuments') || 'documents'}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <FiFileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-800">
                            {doc.document_name || t('labels.lblUntitledDocument') || 'Untitled Document'}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('labels.lblUploadedOn') || 'Uploaded on'}: {formatDate(doc.created_at)}
                          </p>
                          {doc.description && (
                            <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(doc.status)}
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(doc.status)}`}>
                            {getStatusLabel(doc.status)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleViewDocument(doc.document_url)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t('labels.lblView') || 'View'}
                        >
                          <FiEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(doc.document_url, doc.document_name)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={t('labels.lblDownload') || 'Download'}
                        >
                          <FiDownload className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Proof of Address Documents Section */}
          {addresses.length > 0 && (
            <section className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FiFileText className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      {t('labels.lblProveAddress') || 'Proof of Address'}
                    </h3>
                  </div>
                  <span className="text-sm text-gray-600">
                    {addresses.length} {t('labels.lblDocuments') || 'documents'}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="bg-green-100 p-3 rounded-lg">
                          <FiFileText className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-800">
                            {t('labels.lblAddressProof') || 'Address Proof'}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {address.address}, {address.city}, {address.state}
                          </p>
                          <p className="text-xs text-gray-500">
                            {address.postal_code} - {address.country}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(address.status)}
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(address.status)}`}>
                            {getStatusLabel(address.status)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleViewDocument(address.document_url)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t('labels.lblView') || 'View'}
                        >
                          <FiEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(address.document_url, `address-proof-${address.id}.pdf`)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={t('labels.lblDownload') || 'Download'}
                        >
                          <FiDownload className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
        )
      )}

      {/* Company Documents Tab */}
      {activeTab === 'company' && (
        companyDocuments.length === 0 ? (
          <div className="mt-8 bg-blue-50 rounded-lg p-8 text-center">
            <FiFileText className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t('labels.lblNoDocuments') || 'No Documents Found'}
            </h3>
            <p className="text-gray-600">
              {t('labels.lblNoCompanyDocumentsDescription') || 'No company documents have been submitted yet.'}
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            <section className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FiBriefcase className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      {t('labels.lblCompanyDocuments') || 'Company Documents'}
                    </h3>
                  </div>
                  <span className="text-sm text-gray-600">
                    {companyDocuments.length} {t('labels.lblDocuments') || 'documents'}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {companyDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <FiFileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-800">
                            {doc.name || t('labels.lblUntitledDocument') || 'Untitled Document'}
                          </h4>
                          {doc.folder?.name && (
                            <p className="text-xs text-gray-500 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {translateFolderName(doc.folder.name)}
                              </span>
                            </p>
                          )}
                          {doc.issue_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              {t('labels.lblIssueDate') || 'Issue Date'}: {formatDate(doc.issue_date)}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {t('labels.lblUploadedOn') || 'Uploaded on'}: {formatDate(doc.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.signatures && doc.signatures.length > 0 ? (
                            <>
                              <FiCheckCircle className="w-5 h-5 text-green-600" />
                              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {t('labels.lblSigned') || 'Signed'}
                              </span>
                            </>
                          ) : (
                            <>
                              <FiClock className="w-5 h-5 text-gray-600" />
                              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {t('labels.lblUnsigned') || 'Unsigned'}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleViewDocument(doc.s3path)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t('labels.lblView') || 'View'}
                        >
                          <FiEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(doc.s3path, doc.name || `document-${doc.id}.pdf`)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={t('labels.lblDownload') || 'Download'}
                        >
                          <FiDownload className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )
      )}

      {/* Information Note */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ {t('labels.lblDocumentsInfo') || 'Documents are reviewed by our team. You will be notified once the review is complete.'}
        </p>
      </div>
    </>
  );
};

